import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent
} from '@mui/material';
import { Search, BarChart3, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { analysisInsightsApiService, AnalysisInsightResponse, GetAnalysisInsightsParams } from '@/services/api/analysisInsights';
import { useRestaurant } from '@/contexts/RestaurantContext';
import { toast } from 'sonner';

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof AnalysisInsightResponse | 'actions';
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'timestamp', label: 'Date', numeric: false, sortable: true },
  { id: 'analysis_type', label: 'Type', numeric: false, sortable: true },
  { id: 'original_query', label: 'Query', numeric: false, sortable: false },
  { id: 'importance', label: 'Importance', numeric: false, sortable: true },
  { id: 'requires_action', label: 'Action Required', numeric: false, sortable: true },
  { id: 'tags', label: 'Tags', numeric: false, sortable: false },
];

const getImportanceColor = (importance: string) => {
  switch (importance.toLowerCase()) {
    case 'high': return 'error';
    case 'medium': return 'warning';
    case 'low': return 'info';
    default: return 'default';
  }
};

const getAnalysisTypeIcon = (analysisType: string) => {
  switch (analysisType) {
    case 'price_analysis': return <TrendingUp className="h-4 w-4" />;
    case 'supplier_analysis': return <BarChart3 className="h-4 w-4" />;
    case 'trend_analysis': return <TrendingUp className="h-4 w-4" />;
    default: return <BarChart3 className="h-4 w-4" />;
  }
};

const formatAnalysisType = (type: string) => {
  return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === 'timestamp') {
    const aTime = new Date((a as any).timestamp).getTime();
    const bTime = new Date((b as any).timestamp).getTime();
    if (bTime < aTime) return -1;
    if (bTime > aTime) return 1;
    return 0;
  }

  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string | any },
  b: { [key in Key]: number | string | any },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function AnalysisInsights() {
  const navigate = useNavigate();
  const { restaurantProfile } = useRestaurant();
  const [insights, setInsights] = useState<AnalysisInsightResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof AnalysisInsightResponse>('timestamp');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [analysisTypeFilter, setAnalysisTypeFilter] = useState<string>('');
  const [importanceFilter, setImportanceFilter] = useState<string>('');
  const [requiresActionFilter, setRequiresActionFilter] = useState<boolean | null>(null);

  useEffect(() => {
    if (restaurantProfile) {
      fetchInsights();
    }
  }, [restaurantProfile, searchTerm, analysisTypeFilter, importanceFilter, requiresActionFilter, page, rowsPerPage]);

  const fetchInsights = async () => {
    if (!restaurantProfile) return;

    try {
      setLoading(true);
      setError(null);

      const params: GetAnalysisInsightsParams = {
        business_unit_id: restaurantProfile.business_unit_id,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        search: searchTerm || undefined,
        analysis_type: analysisTypeFilter || undefined,
        importance: importanceFilter || undefined,
        requires_action: requiresActionFilter ?? undefined,
      };

      const data = await analysisInsightsApiService.getAnalysisInsights(params);
      setInsights(data);
    } catch (err) {
      setError('Failed to load analysis insights. Please try again later.');
      console.error('Error fetching insights:', err);
      toast.error('Failed to load analysis insights');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property: keyof AnalysisInsightResponse) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (insightId: string) => {
    navigate(`/app/analysis/${insightId}`);
  };

  const sortedInsights = useMemo(() => {
    return [...insights].sort(getComparator(order, orderBy));
  }, [insights, order, orderBy]);

  if (!restaurantProfile) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Please select a business unit to view analysis insights.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <BarChart3 size={32} className="text-primary mr-3" />
          <Typography variant="h4" component="h1">
            Análisis Insights
          </Typography>
        </Box>
        {restaurantProfile && (
          <Card sx={{ minWidth: 200 }}>
            <CardContent sx={{ py: 1, px: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Business Unit
              </Typography>
              <Typography variant="h6" color="primary">
                ID: {restaurantProfile.business_unit_id}
              </Typography>
              <Typography variant="body2">
                {restaurantProfile.business_unit_name || 'Default Unit'}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
        {/* Filters */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <TextField
            placeholder="Search in analysis text..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Analysis Type</InputLabel>
            <Select
              value={analysisTypeFilter}
              label="Analysis Type"
              onChange={(e) => {
                setAnalysisTypeFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="price_analysis">Price Analysis</MenuItem>
              <MenuItem value="supplier_analysis">Supplier Analysis</MenuItem>
              <MenuItem value="product_analysis">Product Analysis</MenuItem>
              <MenuItem value="trend_analysis">Trend Analysis</MenuItem>
              <MenuItem value="general_analysis">General Analysis</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Importance</InputLabel>
            <Select
              value={importanceFilter}
              label="Importance"
              onChange={(e) => {
                setImportanceFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={requiresActionFilter === true}
                indeterminate={requiresActionFilter === null}
                onChange={(e) => {
                  if (requiresActionFilter === null) {
                    setRequiresActionFilter(true);
                  } else if (requiresActionFilter === true) {
                    setRequiresActionFilter(false);
                  } else {
                    setRequiresActionFilter(null);
                  }
                  setPage(0);
                }}
              />
            }
            label="Requires Action"
          />
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof AnalysisInsightResponse)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedInsights.map((insight) => (
                <TableRow
                  key={insight.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                  hover
                  onClick={() => handleRowClick(insight.id)}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(insight.timestamp).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(insight.timestamp).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getAnalysisTypeIcon(insight.analysis_type)}
                      <Typography variant="body2">
                        {formatAnalysisType(insight.analysis_type)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {insight.original_query}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={insight.importance.charAt(0).toUpperCase() + insight.importance.slice(1)}
                      color={getImportanceColor(insight.importance) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {insight.requires_action ? (
                        <>
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          <Typography variant="body2" color="warning.main">
                            Yes
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 text-success" />
                          <Typography variant="body2" color="success.main">
                            No
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {insight.tags.slice(0, 3).map((tag, index) => (
                        <Chip 
                          key={index}
                          label={tag} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                      {insight.tags.length > 3 && (
                        <Chip 
                          label={`+${insight.tags.length - 3}`}
                          size="small" 
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {sortedInsights.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No analysis insights found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={-1} // We don't know the total count, so we use -1
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        />
      </Paper>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Showing {sortedInsights.length} insights for Business Unit {restaurantProfile.business_unit_id}
        </Typography>
      </Box>
    </Box>
  );
}